// src/components/ui/MissedAlertsPanel.tsx
// "While you were away" — shown once after login when notification-service
// holds in-app alerts the operator hasn't seen yet (filtered server-side by
// their Settings > Notifications toggles). Replaces the old emailed digest.
//
// The per-user watermark only advances when the panel is dismissed, so a
// reload before dismissing shows it again. Once the operator has nothing
// pending, a pagehide keepalive POST moves the watermark forward so events
// they watched arrive live are not replayed as "missed" next login.
import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { brand, fonts } from '@/lib/tokens';
import {
  getMissedNotifications,
  markNotificationsSeen,
  markNotificationsSeenOnUnload,
  type MissedNotificationsResponse,
} from '@/lib/services/notifications';

const LABEL_FOR_TYPE: Record<string, string> = {
  'booking.ready_for_review': 'Bookings ready for review',
  'email.received': 'New enquiries',
  'thread_reply.received': 'Client replies',
  'workflow.failed': 'Failed bookings',
  'workflow.quarantined': 'Quarantined bookings',
  'substitution.required': 'Substitutions needed',
  'audit.alert': 'Audit alerts',
};

const ICON_FOR_TYPE: Record<string, string> = {
  'booking.ready_for_review': 'fact_check',
  'email.received': 'mail',
  'thread_reply.received': 'reply',
  'workflow.failed': 'error',
  'workflow.quarantined': 'gpp_maybe',
  'substitution.required': 'swap_horiz',
  'audit.alert': 'policy',
};

const MAX_LISTED = 8;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function MissedAlertsPanel() {
  const [data, setData] = useState<MissedNotificationsResponse | null>(null);
  const [open, setOpen] = useState(false);
  // True while an un-dismissed panel is (or may be) on screen: the unload
  // handler must not advance the watermark past events the operator never saw.
  const pendingRef = useRef(true);

  useEffect(() => {
    let cancelled = false;
    getMissedNotifications()
      .then((res) => {
        if (cancelled) return;
        if (res.events.length > 0) {
          setData(res);
          setOpen(true);
        } else {
          pendingRef.current = false;
        }
      })
      .catch(() => {
        // Best-effort: failing to load the replay must never get in the
        // operator's way. Leave the watermark alone so nothing is lost.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onPageHide = () => {
      if (pendingRef.current) return;
      markNotificationsSeenOnUnload(new Date().toISOString());
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  function dismiss() {
    setOpen(false);
    const through = data?.until;
    if (through) {
      markNotificationsSeen(through)
        .then(() => {
          pendingRef.current = false;
        })
        .catch(() => {
          // Watermark not advanced — the panel simply shows again next time.
          // Keep pendingRef true so unload doesn't skip past unseen events.
        });
    } else {
      pendingRef.current = false;
    }
  }

  if (!data) return null;

  const total = data.events.length;
  const stats = data.daily_stats;
  const listed = data.events.slice(0, MAX_LISTED);

  const body = (
    <div style={{ fontFamily: fonts.sans }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        {Object.entries(data.counts).map(([type, n]) => (
          <span
            key={type}
            className="mono-label"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: brand.accent }}
          >
            <span className="material-symbols-outlined" aria-hidden style={{ fontSize: 16 }}>
              {ICON_FOR_TYPE[type] ?? 'notifications'}
            </span>
            {n} {LABEL_FOR_TYPE[type] ?? type}
          </span>
        ))}
      </div>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 260, overflowY: 'auto' }}>
        {listed.map((e) => (
          <li
            key={e.id}
            style={{ padding: '8px 0', borderTop: '1px solid var(--hairline)' }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: brand.onSurface }}>{e.title}</div>
            <div style={{ fontSize: 12, color: 'rgba(var(--fg-rgb),0.6)' }}>{e.body}</div>
            <div className="mono-label" style={{ marginTop: 2 }}>{formatWhen(e.created_at)}</div>
          </li>
        ))}
      </ul>
      {total > listed.length && (
        <div className="mono-label" style={{ marginTop: 8 }}>
          + {total - listed.length} more
        </div>
      )}

      {stats && (
        <div
          className="mono-label"
          style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--hairline)' }}
        >
          Today: {stats.sent} sent · {stats.rejected} rejected · {stats.pending_approval} pending approval
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      title="While you were away"
      tone="info"
      body={body}
      confirmLabel="Got it"
      cancelLabel={null}
      onConfirm={dismiss}
      onCancel={dismiss}
    />
  );
}
